import * as yup from "yup";
import { useMemo } from "react";
import { Row, Col } from "reactstrap";
import { FormikProps } from "formik";
import { DrawerFormSaveResult, hasClientErrorOperationStatusCode } from "../../../../assets/utils/lib";
import {
    StringHelper,
    ValidationHelper,
} from "../../../../assets/utils/helpers";
import { Constants } from "../../../../assets/utils/lib/constants";
import { LookupDto } from "../../../../assets/utils/lib/lookupDto";
import Drawer from "../../../../assets/vfx/drawer/drawer";
import Form from "../../../../assets/vfx/form/form";
import { useCommunity } from "../../../../context/communityContext";
import { OrganizationDto } from "../../../../services/dto/communityResource/organizationDto";
import { ViewProps } from "./props";

interface OrganizationFormProps {
    data?: OrganizationDto;
    loading?: boolean;
    show: boolean;
    lookups?: ViewProps.OrganizationLookupProps;
    communityId?: string;
    disableCommunity: boolean;
    onHide: () => void;
    onSave: (values: OrganizationDto) => DrawerFormSaveResult;
}

const validationSchema = yup.object().shape({
    communityId: yup.string().required("Community is required."),
    name: yup.string().required("Name is required."),
    summary: yup.string().required("Summary is required."),
    webUrl: yup
        .string()
        .trim()
        // treat empty string as null so it's allowed when optional
        .transform((v) => (v === "" ? null : v))
        .nullable()
        .notRequired()
        // add scheme if missing (supports http/https/mailto/etc if already present)
        .transform((v) =>
            v && !/^[a-zA-Z][a-zA-Z\d+.-]*:\/\//.test(v) ? `https://${v}` : v
        )
        .url("Invalid URL."),
    contactName: yup.string().required("Contact name is required."),
    contactEmail: yup
        .string()
        .required("Contact email is required.")
        .email("Invalid email."),
    contactPhone: ValidationHelper.yupValidatePhone().required(
        "Contact phone is required."
    ),
    addressLine1: yup.string().required("Address line1 is required."),
    city: yup.string().required("City is required."),
    stateId: yup.string().required("State is required."),
    zip: yup.string().required("Zip is required."),
    activationDate: yup
        .date()
        .typeError("Please enter a valid date.")
        .nullable()
        .required("Activation date is required."),
});

export const OrganizationForm = (props: OrganizationFormProps) => {
    let resourceOrganization: OrganizationDto | undefined = props.data;
    const communityContext = useCommunity();
    let communityId = props.communityId;
    if (!communityId) communityId = communityContext.communityId ?? undefined;

    const header: string =
        StringHelper.whenNullOrEmpty(
            resourceOrganization?.id,
            Constants.emptyGuid
        ) === Constants.emptyGuid
            ? "New Community Resource"
            : "Edit Community Resource";

    const onSubmit = (data: OrganizationDto) => {
        const updatedData: OrganizationDto = { ...data };
        return Promise.resolve(props.onSave(updatedData)).then((result) => {
            if (hasClientErrorOperationStatusCode(result)) {
                return;
            }

            props.onHide();
        });
    };
    return (
        <Drawer.Form
            className="w-40"
            show={props.show}
            backdrop={true}
            placement="end"
            scroll={true}
            title={header}
            initialValues={
                resourceOrganization ?? {
                    communityId: communityId,
                }
            }
            validationSchema={validationSchema}
            enableReinitialize
            on_Submit={onSubmit}
            onHide={props.onHide}
        >
            {(formikProps: FormikProps<OrganizationDto>) => {
                const stateId = formikProps.values?.stateId;

                const countyOptions = useMemo(() => {
                    const all = props.lookups?.county ?? [];
                    const filtered = all.filter((x: { parentId?: string | null }) => String(x.parentId ?? "") === String(stateId ?? ""));
                    return LookupDto.mapToOptions(filtered);
                }, [props.lookups?.county, stateId]);

                const handleStateChange = (val: string | undefined) => {
                    formikProps.setFieldValue("stateId", val);
                    formikProps.setFieldValue("countyId", Constants.emptyGuid);
                };

                return (
                    <>
                        <Form.PanelSection iconClassName="bi bi-info-circle" title="1. Basic information">
                            <Form.Text
                                name="name"
                                placeholder="Name"
                                label="Name"
                                isLoading={props.loading}
                                required
                            />
                            <Form.TextArea
                                name="summary"
                                rows={3}
                                label="Summary"
                                isLoading={props.loading}
                                required
                            />

                            <Form.Autocomplete
                                name="categories"
                                label="Category"
                                id="sections"
                                labelKey="name"
                                placeholder="Select Category"
                                multiple={true}
                                options={
                                    props.lookups?.category?.map((opt) => ({
                                        id: opt.key,
                                        name: opt.display,
                                    })) ?? []
                                }
                            />
                        </Form.PanelSection>

                        <Form.PanelSection
                            iconClassName="bi bi-person-lines-fill"
                            title="2. Contact information"
                            style={{ marginTop: 16 }}
                        >
                            <Form.Group label="Address" required>
                                <Row>
                                    <Col xs={12}>
                                        <Form.Text name="addressLine1" placeholder="Address line 1" isLoading={props.loading} />
                                    </Col>
                                    <Col xs={12}>
                                        <Form.Text name="addressLine2" placeholder="Address line 2" isLoading={props.loading} />
                                    </Col>
                                    <Col xs={12} md={4}>
                                        <Form.Text name="city" placeholder="City" isLoading={props.loading} />
                                    </Col>
                                    <Col xs={12} md={4}>
                                        <Form.Dropdown
                                            name="stateId"
                                            blankOption={{
                                                key: Constants.emptyGuid,
                                                display: "--Select state--",
                                                value: Constants.emptyGuid,
                                            }}
                                            options={LookupDto.mapToOptions(props.lookups?.state, undefined, { labelKey: "code" })}
                                            onChange={(e: any) => handleStateChange(e?.target?.value)}
                                            isLoading={props.loading}
                                        />
                                    </Col>
                                    <Col xs={12} md={4}>
                                        <Form.Zip name="zip" placeholder="Zip" isLoading={props.loading} />
                                    </Col>
                                </Row>
                            </Form.Group>

                            <Row>
                                <Col xs={12}>
                                    <Form.Group label="County">
                                        <Form.Dropdown
                                            name="countyId"
                                            disabled={formikProps.values.stateId === Constants.emptyGuid || formikProps.values.stateId === undefined}
                                            isLoading={props.loading}
                                            blankOption={{
                                                key: Constants.emptyGuid,
                                                display: "--Select county--",
                                                value: Constants.emptyGuid,
                                            }}
                                            options={countyOptions}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Row>
                                <Col xs={12}>
                                    <Form.Phone name="publishedPhone" placeholder="Phone" label="Published phone" isLoading={props.loading} />
                                </Col>
                                <Col xs={12}>
                                    <Form.Text name="publishedEmail" placeholder="Email" label="Published email" isLoading={props.loading} />
                                </Col>
                            </Row>
                        </Form.PanelSection>

                        <Form.PanelSection
                            iconClassName="bi bi-person-vcard"
                            title="3. Contact Person"
                            style={{ marginTop: 16 }}
                        >
                            <Row>
                                <Col xs={12}>
                                    <Form.Text name="contactName" placeholder="Name" label="Name" isLoading={props.loading} required />
                                </Col>
                                <Col xs={12}>
                                    <Form.Text name="contactJobTitle" placeholder="Job title" label="Job title" isLoading={props.loading} />
                                </Col>
                                <Col xs={12}>
                                    <Form.Phone name="contactPhone" placeholder="Phone" label="Phone" isLoading={props.loading} required />
                                </Col>
                                <Col xs={12}>
                                    <Form.Text name="contactEmail" placeholder="Email" label="Email" isLoading={props.loading} required />
                                </Col>
                            </Row>
                        </Form.PanelSection>

                        <Form.PanelSection
                            iconClassName="bi bi-card-list"
                            title="4. Details"
                            style={{ marginTop: 16 }}
                        >
                            <Form.Text
                                name="webUrl"
                                placeholder="Web URL"
                                label="Web URL"
                                isLoading={props.loading}
                            />
                            <Form.Date
                                name="activationDate"
                                label="Activation date"
                                placeholder="Enter date"
                                required
                            />
                            <Form.Group
                                label="Logo"
                                note="Recommended size 125 x 125"
                                isLoading={props.loading}
                            >
                                <Form.ImageCrop
                                    name="logo"
                                    loading={props.loading}
                                    aspect={1}
                                    maxOutputWidth={125}
                                    previewMaxHeight={125}
                                    modalTitle="Logo crop"
                                    chooseText="Choose logo"
                                    changeText="Change logo"
                                />
                            </Form.Group>
                        </Form.PanelSection>
                    </>
                );
            }}
        </Drawer.Form>
    );
};